import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Coins, TrendingUp, Lock, Unlock, Clock, FileText,
  ShoppingCart, ArrowLeft, Loader2, Timer, Shield, ChevronRight, CreditCard,
  Download, ExternalLink, CheckCircle, AlertTriangle, Eye, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KBTCoinPortal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [balance, setBalance] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [buyAmount, setBuyAmount] = useState('');
  const [stakeAmount, setStakeAmount] = useState('');
  const [stakeDuration, setStakeDuration] = useState('30d');
  const [purchasing, setPurchasing] = useState(false);
  const [staking, setStaking] = useState(false);
  const [activeTab, setActiveTab] = useState('buy');
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [contractDetail, setContractDetail] = useState(null);
  const [loadingContract, setLoadingContract] = useState(false);

  const headers = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  }), []);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, balRes, purRes, conRes] = await Promise.all([
        fetch(`${API_URL}/api/token/stats`),
        user ? fetch(`${API_URL}/api/token/balance`, { headers: headers() }) : null,
        user ? fetch(`${API_URL}/api/token/purchases`, { headers: headers() }) : null,
        user ? fetch(`${API_URL}/api/token/contracts`, { headers: headers() }) : null,
      ]);
      setStats(await statsRes.json());
      if (balRes?.ok) setBalance(await balRes.json());
      if (purRes?.ok) { const d = await purRes.json(); setPurchases(d.purchases || []); }
      if (conRes?.ok) { const d = await conRes.json(); setContracts(d.contracts || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Verify payment on page load if returning from Stripe
  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');
      const amount = searchParams.get('amount');
      
      if (sessionId && user) {
        setVerifyingPayment(true);
        try {
          const res = await fetch(`${API_URL}/api/token/checkout/verify/${sessionId}`, {
            headers: headers(),
          });
          const data = await res.json();
          
          if (data.status === 'completed') {
            toast.success(`Successfully purchased ${data.amount?.toLocaleString() || amount} KBTCOIN!`);
            // Clear the URL params
            navigate('/kbtcoin', { replace: true });
            fetchData();
          } else if (data.status === 'pending') {
            toast.info('Payment is still processing. Please wait...');
          }
        } catch (e) {
          console.error('Payment verification failed:', e);
        } finally {
          setVerifyingPayment(false);
        }
      }
    };
    
    verifyPayment();
  }, [searchParams, user, navigate, fetchData]);

  const handlePurchase = async () => {
    const amt = parseInt(buyAmount);
    if (!amt || amt < 1) { toast.error('Enter a valid amount'); return; }
    if (!acceptTerms) { 
      toast.error('You must accept the terms and conditions to proceed'); 
      setShowTermsModal(true);
      return; 
    }
    setPurchasing(true);
    try {
      // Create Stripe checkout session
      const res = await fetch(`${API_URL}/api/token/checkout`, {
        method: 'POST', 
        headers: headers(), 
        body: JSON.stringify({ 
          amount: amt,
          success_url: `${window.location.origin}/kbtcoin`,
          cancel_url: `${window.location.origin}/kbtcoin`,
          accept_terms: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail); return; }
      
      // Redirect to Stripe checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (e) { toast.error('Purchase failed'); }
    finally { setPurchasing(false); }
  };

  const viewContractDetail = async (contractId) => {
    setLoadingContract(true);
    try {
      const res = await fetch(`${API_URL}/api/token/contracts/${contractId}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setContractDetail(data);
        setShowContractModal(true);
      } else {
        toast.error('Failed to load contract');
      }
    } catch (e) {
      toast.error('Failed to load contract');
    } finally {
      setLoadingContract(false);
    }
  };

  const downloadContractPDF = (contractId) => {
    window.open(`${API_URL}/api/token/contracts/${contractId}/pdf?token=${localStorage.getItem('token')}`, '_blank');
  };

  const handleStake = async () => {
    const amt = parseInt(stakeAmount);
    if (!amt || amt < 1) { toast.error('Enter a valid amount'); return; }
    setStaking(true);
    try {
      const res = await fetch(`${API_URL}/api/token/stake`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ amount: amt, duration: stakeDuration }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail); return; }
      toast.success(data.message);
      setStakeAmount('');
      fetchData();
    } catch (e) { toast.error('Staking failed'); }
    finally { setStaking(false); }
  };

  const handleUnstake = async (stakeId) => {
    try {
      const res = await fetch(`${API_URL}/api/token/unstake/${stakeId}`, {
        method: 'POST', headers: headers(),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail); return; }
      toast.success(data.message);
      fetchData();
    } catch (e) { toast.error('Unstake failed'); }
  };

  const formatTime = (seconds) => {
    if (seconds <= 0) return 'Unlockable now';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    if (d > 0) return `${d}d ${h}h remaining`;
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m remaining`;
  };

  if (loading) return (
    <div className="min-h-screen bg-[hsl(220,25%,4%)] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[hsl(220,25%,4%)] text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-gray-400">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold" data-testid="kbt-portal-title">KBTCOIN Portal</h1>
            <p className="text-gray-400 text-sm">Buy, stake, and manage your KBTCOIN tokens</p>
          </div>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="token-stats-bar">
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Total Supply</p>
                <p className="text-xl font-bold font-mono text-white">{(stats.total_supply || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider">In Circulation</p>
                <p className="text-xl font-bold font-mono text-yellow-400">{(stats.tokens_in_circulation || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Seed Round</p>
                <div className="flex items-center justify-center gap-2">
                  <p className="text-xl font-bold font-mono text-cyan-400">{(stats.seed_round_sold || 0).toLocaleString()}</p>
                  <span className="text-xs text-gray-500">/ {(stats.seed_round_total || 0).toLocaleString()}</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-1.5 mt-2">
                  <div className="bg-cyan-400 h-1.5 rounded-full transition-all" style={{ width: `${stats.seed_round_progress_pct || 0}%` }} />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Token Price</p>
                <p className="text-xl font-bold font-mono text-green-400">£{stats.token_price_gbp?.toFixed(2)}</p>
                <Badge className={`mt-1 text-xs ${stats.seed_round_open ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {stats.seed_round_open ? 'Seed Round Open' : 'Seed Round Closed'}
                </Badge>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Balance Card (logged in) */}
        {user && balance && (
          <Card className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30 mb-8" data-testid="user-balance-card">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Your Balance</p>
                  <p className="text-3xl font-bold font-mono text-yellow-400">{(balance.balance || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-500">KBT</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Current Value</p>
                  <p className="text-2xl font-bold font-mono text-white">£{(balance.current_value_gbp || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Total Purchased</p>
                  <p className="text-2xl font-bold font-mono text-cyan-400">{(balance.total_purchased || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Total Spent</p>
                  <p className="text-2xl font-bold font-mono text-gray-300">£{(balance.total_spent_gbp || 0).toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!user && (
          <Card className="bg-white/[0.03] border-white/10 mb-8">
            <CardContent className="p-8 text-center">
              <Coins className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Login to Buy KBTCOIN</h3>
              <p className="text-gray-400 mb-4">Create an account or log in to purchase tokens and access staking.</p>
              <Button onClick={() => navigate('/login')} className="bg-yellow-500 hover:bg-yellow-600 text-black">Log In</Button>
            </CardContent>
          </Card>
        )}

        {/* Main Tabs */}
        {user && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white/5 border border-white/10 h-auto flex-wrap gap-1 p-1">
              <TabsTrigger value="buy" data-testid="tab-buy" className="data-[state=active]:bg-yellow-500/20"><ShoppingCart className="w-4 h-4 mr-1" /> Buy</TabsTrigger>
              <TabsTrigger value="stake" data-testid="tab-stake" className="data-[state=active]:bg-yellow-500/20"><Lock className="w-4 h-4 mr-1" /> Stake</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history" className="data-[state=active]:bg-yellow-500/20"><Clock className="w-4 h-4 mr-1" /> History</TabsTrigger>
              <TabsTrigger value="contracts" data-testid="tab-contracts" className="data-[state=active]:bg-yellow-500/20"><FileText className="w-4 h-4 mr-1" /> Contracts</TabsTrigger>
            </TabsList>

            {/* BUY TAB */}
            <TabsContent value="buy" data-testid="buy-tab-content">
              <Card className="bg-white/[0.03] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Buy KBTCOIN</CardTitle>
                  <CardDescription className="text-gray-400">Purchase tokens at the current seed round price of £{stats?.token_price_gbp?.toFixed(2)} per KBT</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!stats?.seed_round_open ? (
                    <div className="text-center py-8">
                      <Lock className="w-10 h-10 text-red-400 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-white mb-1">Seed Round Not Open</h3>
                      <p className="text-gray-400 text-sm">The seed round hasn't been launched yet. Join the waitlist to be notified.</p>
                      <Button className="mt-4" variant="outline" onClick={() => navigate('/crypto')}>Join Waitlist</Button>
                    </div>
                  ) : (
                    <>
                      {verifyingPayment && (
                        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg mb-4 flex items-center gap-3">
                          <Loader2 className="w-5 h-5 text-green-400 animate-spin" />
                          <div>
                            <p className="text-green-400 font-semibold">Verifying Payment...</p>
                            <p className="text-green-400/80 text-sm">Please wait while we confirm your purchase.</p>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-3">
                        <Input
                          type="number" min="1" placeholder="Number of tokens"
                          value={buyAmount} onChange={(e) => setBuyAmount(e.target.value)}
                          className="bg-white/5 border-white/10 text-white flex-1"
                          data-testid="buy-amount-input"
                        />
                        <Button onClick={handlePurchase} disabled={purchasing || verifyingPayment || !acceptTerms} className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-8" data-testid="buy-submit-btn">
                          {purchasing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" /> Pay with Card</>}
                        </Button>
                      </div>
                      {buyAmount && parseInt(buyAmount) > 0 && (
                        <div className="p-4 bg-white/5 rounded-lg flex items-center justify-between">
                          <span className="text-gray-400">Total Cost:</span>
                          <span className="text-xl font-bold font-mono text-white">£{(parseInt(buyAmount) * (stats?.token_price_gbp || 0.10)).toFixed(2)}</span>
                        </div>
                      )}
                      
                      {/* Terms Acceptance */}
                      <div className="p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-lg space-y-3">
                        <div className="flex items-start gap-3">
                          <Checkbox 
                            id="accept-terms" 
                            checked={acceptTerms} 
                            onCheckedChange={setAcceptTerms}
                            className="mt-1 border-yellow-500/50 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
                          />
                          <label htmlFor="accept-terms" className="text-sm text-gray-300 cursor-pointer">
                            I have read and agree to the <button type="button" onClick={() => setShowTermsModal(true)} className="text-yellow-400 underline hover:text-yellow-300">KBTCOIN Purchase Agreement Terms</button>, 
                            the <a href="/whitepaper.html" target="_blank" className="text-yellow-400 underline hover:text-yellow-300">Whitepaper</a>, and acknowledge:
                          </label>
                        </div>
                        <ul className="text-xs text-gray-400 space-y-1 ml-8 list-disc">
                          <li>This purchase is speculative and I may lose my entire investment</li>
                          <li>Tokens are subject to a 6-month cliff and 18-month vesting schedule</li>
                          <li>The purchase is non-refundable once confirmed</li>
                          <li>I am legally permitted to purchase digital assets in my jurisdiction</li>
                        </ul>
                      </div>
                      
                      <p className="text-xs text-gray-500">Remaining in seed round: {(stats?.seed_round_remaining || 0).toLocaleString()} KBT</p>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* STAKE TAB */}
            <TabsContent value="stake" data-testid="stake-tab-content">
              <div className="space-y-6">
                {/* Stake Form */}
                <Card className="bg-white/[0.03] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Stake KBTCOIN</CardTitle>
                    <CardDescription className="text-gray-400">Lock your tokens to earn rewards. Available: {(balance?.balance || 0).toLocaleString()} KBT</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setStakeDuration('30d')}
                        className={`p-4 rounded-lg border text-left transition-all ${stakeDuration === '30d' ? 'border-yellow-500 bg-yellow-500/10' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}
                        data-testid="stake-30d-option"
                      >
                        <p className="text-lg font-bold text-white">5% APY</p>
                        <p className="text-sm text-gray-400">30-Day Lock</p>
                      </button>
                      <button
                        onClick={() => setStakeDuration('365d')}
                        className={`p-4 rounded-lg border text-left transition-all ${stakeDuration === '365d' ? 'border-yellow-500 bg-yellow-500/10' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}
                        data-testid="stake-365d-option"
                      >
                        <p className="text-lg font-bold text-white">30% APY</p>
                        <p className="text-sm text-gray-400">365-Day Lock</p>
                        <Badge className="bg-green-500/20 text-green-400 text-xs mt-1">Best Value</Badge>
                      </button>
                    </div>
                    <div className="flex gap-3">
                      <Input
                        type="number" min="1" placeholder="Amount to stake"
                        value={stakeAmount} onChange={(e) => setStakeAmount(e.target.value)}
                        className="bg-white/5 border-white/10 text-white flex-1"
                        data-testid="stake-amount-input"
                      />
                      <Button onClick={handleStake} disabled={staking} className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-8" data-testid="stake-submit-btn">
                        {staking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Stake'}
                      </Button>
                    </div>
                    {stakeAmount && parseInt(stakeAmount) > 0 && (
                      <div className="p-4 bg-white/5 rounded-lg flex items-center justify-between">
                        <span className="text-gray-400">Estimated Reward:</span>
                        <span className="text-lg font-bold font-mono text-green-400">
                          +{(parseInt(stakeAmount) * (stakeDuration === '30d' ? 0.05 : 0.30) * (stakeDuration === '30d' ? 30 : 365) / 365).toFixed(2)} KBT
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Active Stakes */}
                {balance?.stakes?.length > 0 && (
                  <Card className="bg-white/[0.03] border-white/10">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2"><Timer className="w-5 h-5 text-yellow-400" /> Active Stakes</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {balance.stakes.map((s) => (
                        <div key={s.stake_id} className="p-4 rounded-lg bg-white/5 border border-white/10" data-testid={`stake-${s.stake_id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="text-white font-bold font-mono">{s.amount.toLocaleString()} KBT</p>
                              <p className="text-xs text-gray-400">{s.label}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-green-400 font-mono text-sm">+{s.estimated_reward} KBT reward</p>
                              <p className="text-xs text-gray-500">Staked: {new Date(s.staked_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-2">
                              {s.is_unlockable ? (
                                <Badge className="bg-green-500/20 text-green-400"><Unlock className="w-3 h-3 mr-1" /> Ready to Unlock</Badge>
                              ) : (
                                <Badge className="bg-white/10 text-gray-300"><Lock className="w-3 h-3 mr-1" /> {formatTime(s.time_remaining_seconds)}</Badge>
                              )}
                            </div>
                            {s.is_unlockable && (
                              <Button size="sm" onClick={() => handleUnstake(s.stake_id)} className="bg-green-500 hover:bg-green-600 text-black">
                                Unstake
                              </Button>
                            )}
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-1 mt-2">
                            <div
                              className="bg-yellow-400 h-1 rounded-full transition-all"
                              style={{ width: `${Math.max(0, 100 - (s.time_remaining_seconds / (s.duration === '30d' ? 2592000 : 31536000)) * 100)}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* HISTORY TAB */}
            <TabsContent value="history" data-testid="history-tab-content">
              <Card className="bg-white/[0.03] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Purchase History</CardTitle>
                </CardHeader>
                <CardContent>
                  {purchases.length > 0 ? (
                    <div className="space-y-3">
                      {purchases.map((p) => (
                        <div key={p.purchase_id} className="flex items-center justify-between p-4 rounded-lg bg-white/5" data-testid={`purchase-${p.purchase_id}`}>
                          <div>
                            <p className="text-white font-medium">{p.amount.toLocaleString()} KBT</p>
                            <p className="text-xs text-gray-500">{new Date(p.created_at).toLocaleString()}</p>
                            <p className="text-xs text-gray-600 font-mono">{p.purchase_id}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-mono">£{(p.total_cost_gbp || 0).toFixed(2)}</p>
                            <p className="text-xs text-gray-500">@ £{(p.price_per_token_gbp || 0).toFixed(2)}/KBT</p>
                            <Badge className="mt-1 bg-green-500/20 text-green-400 text-xs">{p.status}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-8">No purchases yet</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* CONTRACTS TAB */}
            <TabsContent value="contracts" data-testid="contracts-tab-content">
              <Card className="bg-white/[0.03] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2"><Shield className="w-5 h-5 text-yellow-400" /> Smart Contracts</CardTitle>
                  <CardDescription className="text-gray-400">Your KBTCOIN purchase agreements with full legal terms</CardDescription>
                </CardHeader>
                <CardContent>
                  {contracts.length > 0 ? (
                    <div className="space-y-4">
                      {contracts.map((c) => (
                        <div
                          key={c.contract_id}
                          className="p-5 rounded-xl bg-gradient-to-r from-white/5 to-white/[0.02] border border-white/10 hover:border-yellow-500/30 transition-all"
                          data-testid={`contract-${c.contract_id}`}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <p className="text-white font-semibold flex items-center gap-2">
                                <FileText className="w-4 h-4 text-yellow-400" />
                                Purchase Agreement
                              </p>
                              <p className="text-xs text-gray-500 font-mono mt-1">{c.contract_id}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">{c.status}</Badge>
                              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">v{c.version || '1.0'}</Badge>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div className="p-3 bg-white/5 rounded-lg text-center">
                              <p className="text-yellow-400 font-bold text-lg">{(c.token_details?.tokens_purchased || c.terms?.tokens_purchased || 0).toLocaleString()}</p>
                              <p className="text-xs text-gray-500">Tokens</p>
                            </div>
                            <div className="p-3 bg-white/5 rounded-lg text-center">
                              <p className="text-white font-bold text-lg">£{(c.token_details?.total_consideration_gbp || c.terms?.total_cost_gbp || 0).toFixed(2)}</p>
                              <p className="text-xs text-gray-500">Total Value</p>
                            </div>
                            <div className="p-3 bg-white/5 rounded-lg text-center">
                              <p className="text-cyan-400 font-bold text-lg">24 mo</p>
                              <p className="text-xs text-gray-500">Vesting Period</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                            <div className="flex justify-between p-2 bg-white/[0.02] rounded">
                              <span className="text-gray-500">Issuer</span>
                              <span className="text-white">{c.parties?.issuer?.legal_name || c.parties?.issuer?.name || 'Knowledge Based Trading Ltd'}</span>
                            </div>
                            <div className="flex justify-between p-2 bg-white/[0.02] rounded">
                              <span className="text-gray-500">Network</span>
                              <span className="text-white">{c.network?.name || c.network || 'Ethereum'}</span>
                            </div>
                            <div className="flex justify-between p-2 bg-white/[0.02] rounded">
                              <span className="text-gray-500">Cliff Period</span>
                              <span className="text-white">6 months</span>
                            </div>
                            <div className="flex justify-between p-2 bg-white/[0.02] rounded">
                              <span className="text-gray-500">Created</span>
                              <span className="text-white">{new Date(c.created_at || c.effective_date).toLocaleDateString()}</span>
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-1 border-white/20 hover:bg-white/10"
                              onClick={() => viewContractDetail(c.contract_id)}
                              disabled={loadingContract}
                            >
                              {loadingContract ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                              View Full Contract
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-1 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                              onClick={() => downloadContractPDF(c.contract_id)}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download PDF
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400 mb-2">No contracts yet</p>
                      <p className="text-gray-500 text-sm">Purchase tokens to generate your first smart contract with full legal terms.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Terms Modal */}
      <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
        <DialogContent className="bg-[hsl(220,20%,8%)] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-400" />
              KBTCOIN Purchase Agreement Terms
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Please read and understand these terms before purchasing KBTCOIN tokens
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <h4 className="font-semibold text-yellow-400 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Risk Warning
              </h4>
              <p className="text-gray-300">
                Digital assets carry significant risks including market volatility, regulatory changes, technology failures, and potential total loss of value. Only invest what you can afford to lose.
              </p>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-semibold text-white">Key Terms:</h4>
              <div className="space-y-2 text-gray-300">
                <p><strong className="text-white">1. Token Delivery:</strong> Tokens will be delivered upon the Token Generation Event (TGE), expected Q3 2027.</p>
                <p><strong className="text-white">2. Vesting Schedule:</strong> 6-month cliff period from TGE, followed by 18-month linear vesting with monthly releases.</p>
                <p><strong className="text-white">3. Non-Refundable:</strong> All purchases are final and non-refundable once payment is confirmed.</p>
                <p><strong className="text-white">4. Wallet Registration:</strong> You must register a valid Ethereum wallet address before TGE to receive tokens.</p>
                <p><strong className="text-white">5. Price Disclaimer:</strong> Token value at delivery may differ significantly from purchase price.</p>
                <p><strong className="text-white">6. Jurisdiction:</strong> You confirm you are legally permitted to purchase digital assets in your jurisdiction.</p>
                <p><strong className="text-white">7. Governing Law:</strong> This agreement is governed by the laws of England and Wales.</p>
              </div>
            </div>
            
            <div className="p-4 bg-white/5 rounded-lg">
              <h4 className="font-semibold text-white mb-2">Issuer Information:</h4>
              <p className="text-gray-400 text-xs">
                Knowledge Based Trading Ltd (t/a Traders Capital)<br />
                Company Number: 17040814<br />
                Registered Address: 167-169 Great Portland Street, London, W1W 5PF, United Kingdom
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTermsModal(false)} className="border-white/20">
              Close
            </Button>
            <Button 
              onClick={() => { setAcceptTerms(true); setShowTermsModal(false); }}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              I Accept These Terms
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract Detail Modal */}
      <Dialog open={showContractModal} onOpenChange={setShowContractModal}>
        <DialogContent className="bg-[hsl(220,20%,8%)] border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-yellow-400" />
              Smart Contract Details
            </DialogTitle>
            <DialogDescription className="text-gray-400 font-mono text-xs">
              {contractDetail?.contract_id}
            </DialogDescription>
          </DialogHeader>
          
          {contractDetail && (
            <div className="space-y-6">
              {/* Parties */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-cyan-400" /> Issuer
                  </h4>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-400">Legal Name: <span className="text-white">{contractDetail.parties?.issuer?.legal_name}</span></p>
                    <p className="text-gray-400">Trading As: <span className="text-white">{contractDetail.parties?.issuer?.trading_as}</span></p>
                    <p className="text-gray-400">Company #: <span className="text-white">{contractDetail.parties?.issuer?.company_number}</span></p>
                    <p className="text-gray-400">Jurisdiction: <span className="text-white">{contractDetail.parties?.issuer?.jurisdiction}</span></p>
                  </div>
                </div>
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" /> Purchaser
                  </h4>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-400">Name: <span className="text-white">{contractDetail.parties?.purchaser?.name}</span></p>
                    <p className="text-gray-400">Email: <span className="text-white">{contractDetail.parties?.purchaser?.email}</span></p>
                    <p className="text-gray-400">Accepted: <span className="text-white">{contractDetail.parties?.purchaser?.acceptance_timestamp ? new Date(contractDetail.parties.purchaser.acceptance_timestamp).toLocaleString() : 'N/A'}</span></p>
                  </div>
                </div>
              </div>
              
              {/* Token Details */}
              <div className="p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
                <h4 className="font-semibold text-yellow-400 mb-3">Token Purchase Details</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-white">{(contractDetail.token_details?.tokens_purchased || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">KBT Tokens</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">£{(contractDetail.token_details?.price_per_token_gbp || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Price/Token</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-400">£{(contractDetail.token_details?.total_consideration_gbp || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Total Paid</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-cyan-400">{(contractDetail.token_details?.allocation_percentage || 0).toFixed(4)}%</p>
                    <p className="text-xs text-gray-500">Allocation</p>
                  </div>
                </div>
              </div>
              
              {/* Vesting */}
              <div className="p-4 bg-white/5 rounded-lg">
                <h4 className="font-semibold text-white mb-3">Vesting Schedule</h4>
                <div className="grid md:grid-cols-4 gap-3 text-sm">
                  <div className="p-2 bg-white/5 rounded text-center">
                    <p className="text-cyan-400 font-semibold">6 months</p>
                    <p className="text-xs text-gray-500">Cliff Period</p>
                  </div>
                  <div className="p-2 bg-white/5 rounded text-center">
                    <p className="text-cyan-400 font-semibold">18 months</p>
                    <p className="text-xs text-gray-500">Vesting Duration</p>
                  </div>
                  <div className="p-2 bg-white/5 rounded text-center">
                    <p className="text-cyan-400 font-semibold">Monthly</p>
                    <p className="text-xs text-gray-500">Release Schedule</p>
                  </div>
                  <div className="p-2 bg-white/5 rounded text-center">
                    <p className="text-cyan-400 font-semibold">Q3 2027</p>
                    <p className="text-xs text-gray-500">Expected TGE</p>
                  </div>
                </div>
              </div>
              
              {/* Terms */}
              <div className="p-4 bg-white/5 rounded-lg">
                <h4 className="font-semibold text-white mb-3">Terms & Conditions</h4>
                <div className="space-y-3 text-sm max-h-60 overflow-y-auto">
                  {contractDetail.terms_and_conditions?.map((term, i) => (
                    <div key={i} className="pb-2 border-b border-white/5 last:border-0">
                      <p className="font-medium text-white">{term.clause}</p>
                      <p className="text-gray-400 text-xs mt-1">{term.text}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Signatures */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">Issuer Signature</h4>
                  <p className="text-xs text-gray-400 font-mono break-all">{contractDetail.signatures?.issuer?.digital_signature || 'N/A'}</p>
                  <p className="text-xs text-gray-500 mt-1">{contractDetail.signatures?.issuer?.timestamp ? new Date(contractDetail.signatures.issuer.timestamp).toLocaleString() : 'N/A'}</p>
                </div>
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">Purchaser Signature</h4>
                  <p className="text-xs text-gray-400 font-mono break-all">{contractDetail.signatures?.purchaser?.digital_signature || 'N/A'}</p>
                  <p className="text-xs text-gray-500 mt-1">{contractDetail.signatures?.purchaser?.timestamp ? new Date(contractDetail.signatures.purchaser.timestamp).toLocaleString() : 'N/A'}</p>
                </div>
              </div>
              
              {/* Document Hash */}
              <div className="p-3 bg-black/30 rounded-lg text-center">
                <p className="text-xs text-gray-500">Document Hash</p>
                <p className="text-xs font-mono text-gray-400">{contractDetail.document_hash}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContractModal(false)} className="border-white/20">
              Close
            </Button>
            {contractDetail && (
              <Button 
                onClick={() => downloadContractPDF(contractDetail.contract_id)}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KBTCoinPortal;
